import React, { useEffect, useState } from 'react';
import {
  Shield,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Plus,
  Loader2,
  Calendar,
  ChevronDown,
  ChevronUp,
  Package,
  Tag,
  MapPin,
} from 'lucide-react';
import { Button } from '../../components/ui';
import { cn } from '../../lib/utils';
import api from '../../services/api';
import { getAllClaims, fileClaim, type WarrantyClaim } from '../../services/warranty.api';
import { toAbsoluteImageUrl } from '../../services/asset.api';
import { useProperty } from '../../contexts/PropertyContext';
import { getHeroClass } from '../../lib/design-system';

const statusConfig: Record<string, { label: string; icon: React.ElementType; cls: string }> = {
  submitted:    { label: 'Submitted',    icon: Clock,        cls: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900' },
  under_review: { label: 'Under Review', icon: AlertCircle,  cls: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/20 dark:text-yellow-400' },
  approved:     { label: 'Approved',     icon: CheckCircle2, cls: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400' },
  rejected:     { label: 'Rejected',     icon: XCircle,      cls: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400' },
  resolved:     { label: 'Resolved',     icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = statusConfig[status] ?? statusConfig.submitted;
  const Icon = cfg.icon;
  return (
    <span className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium', cfg.cls)}>
      <Icon className="h-3 w-3" />
      {cfg.label}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Claim Detail Panel ────────────────────────────────────────────────────────
function ClaimDetail({ claim }: { claim: WarrantyClaim }) {
  const assetImage = toAbsoluteImageUrl(claim.asset?.productImageUrl ?? claim.asset?.imageUrl);
  return (
    <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 gap-6">
      {/* Asset Info */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Asset</p>
        {claim.asset ? (
          <div className="flex items-start gap-3">
            {assetImage ? (
              <img src={assetImage} alt={claim.asset.name} referrerPolicy="no-referrer" className="h-16 w-16 rounded-xl object-cover border flex-shrink-0 bg-muted" />
            ) : (
              <div className="h-16 w-16 rounded-xl border bg-muted/50 flex items-center justify-center flex-shrink-0">
                <Package className="h-7 w-7 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-semibold text-foreground">{claim.asset.name}</p>
              {claim.asset.brand && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Tag className="h-3 w-3" />
                  {claim.asset.brand}
                  {claim.asset.model && <span className="text-muted-foreground/60">· {claim.asset.model}</span>}
                </div>
              )}
              {claim.asset.category && (
                <p className="text-xs text-muted-foreground capitalize">{claim.asset.category}</p>
              )}
              {claim.asset.location && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  {claim.asset.location}
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No asset linked</p>
        )}
      </div>

      {/* Plan Info */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Warranty Plan</p>
        {claim.plan ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-foreground">{claim.plan.planName}</p>
            <p className="text-xs text-muted-foreground">Provider: {claim.plan.provider}</p>
            <p className="text-xs text-muted-foreground capitalize">Coverage: {claim.plan.coverageType}</p>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {formatDate(claim.plan.startDate)} – {formatDate(claim.plan.endDate)}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No plan info</p>
        )}
      </div>

      {/* Full claim detail row */}
      <div className="sm:col-span-2 space-y-2">
        {claim.description && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Description</p>
            <p className="text-sm text-foreground">{claim.description}</p>
          </div>
        )}
        {claim.resolutionNote && (
          <div className="p-3 rounded-lg bg-muted/50 border">
            <p className="text-xs font-semibold text-foreground mb-0.5">Resolution Note</p>
            <p className="text-sm text-muted-foreground">{claim.resolutionNote}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── File Claim Modal ──────────────────────────────────────────────────────────
interface FileClaimModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (claim: WarrantyClaim) => void;
  homeId: string;
}

function FileClaimModal({ open, onClose, onSuccess, homeId }: Readonly<FileClaimModalProps>) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [incidentDate, setIncidentDate] = useState('');
  const [planId, setPlanId] = useState('');
  const [plans, setPlans] = useState<Array<{ id: string; planName?: string; provider?: string }>>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle(''); setDescription(''); setIncidentDate(''); setPlanId(''); setError(null);
    if (!homeId) return;
    setLoadingPlans(true);
    api.get<Array<{ id: string; planName?: string; provider?: string }>>(`/v1/warranty?homeId=${homeId}`)
      .then(list => {
        setPlans(list);
        if (list.length === 1) setPlanId(list[0].id);
      })
      .catch(() => setPlans([]))
      .finally(() => setLoadingPlans(false));
  }, [open, homeId]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!planId) { setError('Please select a warranty/protection plan.'); return; }
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!incidentDate) { setError('Incident date is required.'); return; }
    setError(null);
    setSubmitting(true);
    try {
      const claim = await fileClaim(planId, {
        title: title.trim(),
        description: description.trim() || undefined,
        incidentDate: new Date(incidentDate).toISOString(),
      });
      onSuccess(claim);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit claim. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 top-16 z-40 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-background rounded-2xl shadow-xl w-full max-w-md border flex flex-col max-h-full">
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold">File a Warranty Claim</h2>
              <p className="text-xs text-muted-foreground">Submit a replacement or repair claim</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Plan selector */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Warranty / Protection Plan</label>
            {loadingPlans ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
              </div>
            ) : plans.length === 0 ? (
              <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                No active warranty or protection plans found for this home. Add one first to file a claim.
              </p>
            ) : (
              <select
                value={planId}
                onChange={e => setPlanId(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                required
              >
                <option value="">Select a plan…</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.planName ?? p.provider ?? p.id}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Claim Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Refrigerator stopped cooling"
              required
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Description <span className="text-muted-foreground font-normal">(optional)</span></label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Describe the issue in detail…"
              rows={3}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
            />
          </div>

          {/* Incident date */}
          <div>
            <label className="text-sm font-medium mb-1.5 block">Incident Date</label>
            <input
              type="date"
              value={incidentDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={e => setIncidentDate(e.target.value)}
              required
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>
          )}

          <div className="flex gap-3 pt-1">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="flex-1" disabled={submitting || plans.length === 0}>
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting…</> : 'Submit Claim'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Claims Page ──────────────────────────────────────────────────────────
export function ClaimsPage() {
  const { currentProperty } = useProperty();
  const homeId = currentProperty?.id ?? '';

  const [claims, setClaims] = useState<WarrantyClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [successClaim, setSuccessClaim] = useState<WarrantyClaim | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllClaims(homeId || undefined);
      setClaims(data);
    } catch {
      setError('Failed to load claims. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [homeId]);

  const handleSuccess = (claim: WarrantyClaim) => {
    setSuccessClaim(claim);
    setClaims(prev => [claim, ...prev]);
    setTimeout(() => setSuccessClaim(null), 5000);
  };

  const activeCount = claims.filter(c => c.status === 'submitted' || c.status === 'under_review').length;
  const resolvedCount = claims.filter(c => c.status === 'resolved' || c.status === 'approved').length;

  return (
    <div className="min-h-screen bg-background">
      {/* ── Header Section ── */}
      <section className="bg-gradient-to-b from-background to-muted/20 border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h1 className={getHeroClass('main')}>My Claims</h1>
            <p className="text-muted-foreground mt-1">Track your warranty and protection plan claims</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            {/* Stats */}
            <div className="flex gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-background">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{claims.length} Total</span>
              </div>
              {activeCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
                  <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-blue-700 dark:text-blue-400">{activeCount} Active</span>
                </div>
              )}
              {resolvedCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900">
                  <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  <span className="font-medium text-green-700 dark:text-green-400">{resolvedCount} Resolved</span>
                </div>
              )}
            </div>
            <Button onClick={() => setShowModal(true)} className="gap-2 shrink-0">
              <Plus className="h-4 w-4" />
              File a Claim
            </Button>
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
        {/* Success banner */}
        {successClaim && (
          <div className="rounded-xl border border-green-200 bg-green-50 dark:bg-green-950/20 p-4 flex items-start gap-3 mb-6">
            <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-700 dark:text-green-400">Claim submitted successfully!</p>
              <p className="text-xs text-green-600 dark:text-green-500 mt-0.5">
                Claim #{successClaim.claimNumber} — we'll review it and get back to you.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading claims…</p>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-red-700">{error}</p>
            <button onClick={load} className="mt-3 text-xs text-red-600 underline hover:no-underline">Try again</button>
          </div>
        ) : claims.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-border bg-muted/10 p-16 flex flex-col items-center gap-4 text-center">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
              <Shield className="h-8 w-8 text-muted-foreground" />
            </div>
            <div>
              <p className="text-base font-semibold text-foreground">No claims yet</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                File a claim when you need a replacement or repair covered by your warranty or protection plan.
              </p>
            </div>
            <Button onClick={() => setShowModal(true)} className="gap-2 mt-2">
              <Plus className="h-4 w-4" />
              File Your First Claim
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {claims.map(claim => {
              const isExpanded = expandedId === claim.id;
              return (
                <div
                  key={claim.id}
                  className="rounded-xl border bg-card transition-shadow hover:shadow-sm"
                >
                  {/* ── Claim Row (clickable) ── */}
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : claim.id)}
                    className="w-full text-left p-5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground">{claim.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            #{claim.claimNumber} &middot; Filed {formatDate(claim.createdAt)}
                          </p>
                          {claim.asset && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {claim.asset.name}{claim.asset.brand ? ` · ${claim.asset.brand}` : ''}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            Incident: {formatDate(claim.incidentDate)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <StatusBadge status={claim.status} />
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    </div>
                  </button>

                  {/* ── Expanded Detail ── */}
                  {isExpanded && (
                    <div className="px-5 pb-5">
                      <ClaimDetail claim={claim} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <FileClaimModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={handleSuccess}
        homeId={homeId}
      />
    </div>
  );
}
